import { useGlobalAction, useSession, useUser } from "@gadgetinc/react";
import { createContext, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { setSentryUser } from "../instrument";

export const CustomerContext = createContext({});

/**
 * @param { children: import("react").ReactNode } props The props passed to the React functional component
 *
 * React component that fetches shop and subscription data
 * Key features:
 *  - Sets the number of trial days left for this shop
 *  - Allows children to access the context from this provider
 *
 * @returns { import("react").ReactElement } A React functional component
 */
const CustomerProvider = ({ children }) => {
  const user = useUser(null, {
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      lastSignedIn: true,
      phone: true,
      phoneVerified: true,
      lastPhoneVerifiedAt: true,
      lastTestPhoneNumbers: true,
      isBlocked: true,
      customerId: true,
      dismissedLibraryFlowsAutomationsAt: true,
      dismissedLibraryFlowsCampaignsAt: true,
      customer: {
        id: true,
        name: true,
        sendingRatePerMinute: true,
        apiKey360Dialog: true,
        wabaId: true,
        waAccessToken: true,
        customFields: true,
        phoneNumberId: true,
        phoneNumber: true,
        codeForLogin: true,
        klaviyoToken: true,
        klaviyoAccessToken: true,
        apiKeySimio: true,
        apiKeyGorgias: true,
        apiKeySplio: true,
        apiKeyHubspot: true,
        hasApiKeyBrevo: true,
        brevoPhoneAttribute: true,
        apiKeyZendesk: true,
        crispWebsiteId: true,
        codeForAlcmeon: true,
        alcmeonCompanyId: true,
        shouldEncryptPI: true,
        isGaiaActivated: true,
        gaiaWebsiteDatasources: true,
        isStripeSubscribed: true,
        isSubscribed: true,
        isCloudApi: true,
        emailsForNotifications: true,
        channelId360Dialog: true,
        unreadConversations: true,
        scheduledCampaignCount: true,
        isExternalTurnoverSplioActivated: true,
        onboardingProgress: true,
        optinsCount: true,
        shopId: true,
        shouldPostponeMessages: true,
        shouldPreventAutomations: true,
        limitMarketingPressure: true,
        isAutomaticUtmActivated: true,
        automaticUtm: true,
        is2faActivated: true,
        shouldHidePersonalData: true,
        azureTenantId: true,
        automaticReplyNodeId: true,
        shouldPreventMultipleAutoReplySendings: true,
        quality: true,
        isExportCampaignsDataGoogleSheetActivated: true,
        campaignsDataSpreadsheetUrl: true,
        messagingLimit: true,
        deliveryHours: true,
        restrictionDate: true,
        restrictionInfo: true,
        violationType: true,
        lastMessageFailedForPaymentIssueAt: true,
        lastCheckPaymentMethodAt: true,
      },
    },
  });
  const session = useSession();
  const [shop, setShop] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const shopLoadedRef = useRef(false);

  const [, setCurrentSession] = useGlobalAction(api.setCurrentSession);

  const refreshCustomer = async () => {
    if (user?.customer?.id) {
      try {
        const updatedCustomer = await api.customer.findOne(user.customer.id, {
          select: {
            id: true,
            name: true,
            sendingRatePerMinute: true,
            apiKey360Dialog: true,
            wabaId: true,
            waAccessToken: true,
            customFields: true,
            phoneNumberId: true,
            phoneNumber: true,
            codeForLogin: true,
            klaviyoToken: true,
            klaviyoAccessToken: true,
            apiKeySimio: true,
            apiKeyGorgias: true,
            apiKeySplio: true,
            apiKeyHubspot: true,
            hasApiKeyBrevo: true,
            brevoPhoneAttribute: true,
            apiKeyZendesk: true,
            crispWebsiteId: true,
            codeForAlcmeon: true,
            alcmeonCompanyId: true,
            shouldEncryptPI: true,
            isGaiaActivated: true,
            gaiaWebsiteDatasources: true,
            isStripeSubscribed: true,
            isSubscribed: true,
            isCloudApi: true,
            emailsForNotifications: true,
            channelId360Dialog: true,
            unreadConversations: true,
            remainingConversations: true,
            scheduledCampaignCount: true,
            isExternalTurnoverSplioActivated: true,
            onboardingProgress: true,
            optinsCount: true,
            shopId: true,
            shouldPostponeMessages: true,
            shouldPreventAutomations: true,
            limitMarketingPressure: true,
            isAutomaticUtmActivated: true,
            automaticUtm: true,
            is2faActivated: true,
            shouldHidePersonalData: true,
            azureTenantId: true,
            automaticReplyNodeId: true,
            shouldPreventMultipleAutoReplySendings: true,
            quality: true,
            isExportCampaignsDataGoogleSheetActivated: true,
            campaignsDataSpreadsheetUrl: true,
            messagingLimit: true,
            deliveryHours: true,
            restrictionDate: true,
            restrictionInfo: true,
            violationType: true,
            lastMessageFailedForPaymentIssueAt: true,
            lastCheckPaymentMethodAt: true,
          },
        });
        setCustomerData(updatedCustomer);
      } catch (error) {
        console.error("Error refreshing customer data:", error);
      }
    }
  };

  useEffect(() => {
    if (user?.customer) {
      setCustomerData(user.customer);
    }
  }, [user]);

  useEffect(() => {
    const loadShopWithRetry = async (shopId, retries = 3) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const shopData = await api.shopifyShop.findOne(shopId, {
            select: {
              id: true,
              name: true,
              domain: true,
              myshopifyDomain: true,
            },
          });
          if (shopData) {
            setShop(shopData);
            return true;
          }
        } catch (error) {
          if (attempt === retries) {
            console.error(
              "Failed to load shop after",
              retries,
              "attempts",
              error,
            );
          }
        }
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
        }
      }
      return false;
    };

    if (user?.customer && session) {
      setCurrentSession({
        sessionId: session.id,
        userId: user.id,
      });
      setSentryUser(user);

      if (user.customer.shopId && !shopLoadedRef.current) {
        shopLoadedRef.current = true;
        loadShopWithRetry(user.customer.shopId);
      }
    } else if (!user) {
      setSentryUser(null);
      shopLoadedRef.current = false;
      setShop(null);
    }
  }, [user, session]);

  return (
    <CustomerContext.Provider
      value={{
        user: user,
        customer: customerData || user?.customer,
        shop: shop,
        refreshCustomer,
        integrations: {
          shopify: shop?.id || customerData?.shopId || user?.customer?.shopId ? true : false,
          klaviyo:
            customerData?.klaviyoToken || customerData?.klaviyoAccessToken ||
            user?.customer?.klaviyoToken || user?.customer?.klaviyoAccessToken
              ? true
              : false,
          gorgias: customerData?.apiKeyGorgias || user?.customer?.apiKeyGorgias ? true : false,
          splio: customerData?.apiKeySplio || user?.customer?.apiKeySplio ? true : false,
          hubspot: customerData?.apiKeyHubspot || user?.customer?.apiKeyHubspot ? true : false,
          crisp: customerData?.crispWebsiteId || user?.customer?.crispWebsiteId ? true : false,
          zendesk: customerData?.apiKeyZendesk || user?.customer?.apiKeyZendesk ? true : false,
          brevo: customerData?.hasApiKeyBrevo || user?.customer?.hasApiKeyBrevo ? true : false,
        },
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};

CustomerProvider.displayName = "CustomerProvider";

export default CustomerProvider;
